import React, { useEffect, useRef, useState } from 'react';
import { Mail, Phone, MapPin, Calendar, MessageSquare, Send, ArrowRight, Sun, Moon, Sparkles, Github, Linkedin, Instagram, Clock, CheckCircle, Globe } from 'lucide-react';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import { useTheme } from './src/ThemeContext.jsx';

/**
 * Contact Me — Standalone Page (Advanced 3D/hover UX)
 * - Matches AboutMe page UI/UX with same navbar and theming
 * - 3D tilt + glow effects tuned for BOTH light and dark modes
 * - Parallax sparkles, magnetic buttons; respects reduced motion
 */

export default function ContactPage() {
  const { darkMode, toggleTheme } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    project: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', email: '', company: '', project: '', message: '' });
    }, 3000);
  };

  return (
    <div className={darkMode ? 'min-h-screen bg-[#0a0c10] text-white' : 'min-h-screen bg-white text-gray-900'}>
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[60] px-3 py-2 rounded-md bg-indigo-600 text-white">Skip to content</a>

      {/* NAV — same as AboutMe page */}
      <header className="sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto mt-4 px-4 sm:px-6">
          <div className={`backdrop-blur rounded-2xl shadow-sm flex items-center justify-between px-4 sm:px-6 py-3 ${darkMode ? 'bg-white/5 border border-white/10' : 'bg-white/80 border border-white/90'}`}>
            <a href="/" className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-semibold text-white ${darkMode ? 'bg-gradient-to-br from-indigo-500 to-cyan-400' : 'bg-gradient-to-br from-indigo-600 to-cyan-500 drop-shadow'}`}>ST</div>
              <div className="leading-tight">
                <div className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Sikandar Tareen</div>
                <div className={`text-[11px] ${darkMode ? 'text-white/60' : 'text-gray-600'}`}>Agentic AI Engineer</div>
              </div>
            </a>
            <div className={`hidden md:flex items-center gap-6 text-sm ${darkMode ? 'text-white/70' : 'text-gray-700'}`}>
              <a href="/" className={`${darkMode ? 'hover:text-white' : 'hover:text-black'}`}>Home</a>
              <a href="/#projects" className={`${darkMode ? 'hover:text-white' : 'hover:text-black'}`}>Projects</a>
              <a href="/blog" className={`${darkMode ? 'hover:text-white' : 'hover:text-black'}`}>Blog</a>
              <a href="/about-me" className={`${darkMode ? 'hover:text-white' : 'hover:text-black'}`}>About</a>

              <a href="/contact" className={`${darkMode ? 'text-white' : 'text-black'} font-medium`}>Contact</a>
            </div>
          </div>
        </nav>
      </header>

      {/* PARALLAX SPARKLES */}
      <ParallaxField darkMode={darkMode} />

      <main id="main" className="max-w-7xl mx-auto px-4 sm:px-6 pb-20 pt-10 [perspective:1200px]">
        {/* Hero */}
        <section className="relative overflow-visible">
          <div className={`absolute -top-24 -left-24 w-[42rem] h-[42rem] rounded-full blur-3xl ${darkMode ? 'bg-indigo-600/20' : 'bg-indigo-200/60'}`}></div>
          <div className={`absolute -bottom-32 -right-16 w-[36rem] h-[36rem] rounded-full blur-3xl ${darkMode ? 'bg-cyan-500/20' : 'bg-cyan-200/60'}`}></div>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="relative z-10">
            <h1 className="text-4xl sm:text-5xl font-extrabold">Let's Work Together</h1>
            <p className={`mt-3 text-lg max-w-2xl ${darkMode ? 'text-white/80' : 'text-gray-700'}`}>Ready to transform your ideas into intelligent, autonomous AI solutions? I'm here to help you build cutting-edge agentic systems that drive real business value.</p>

          </motion.div>
        </section>

        {/* Contact Methods */}
        <section className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: <Mail />, title: 'Email Me', desc: 'Provide as much details in the Email as possible.', action: 'sikandarktareenwork@gmail.com', type: 'button' },
            { icon: <Calendar />, title: 'Schedule a Video Call', desc: 'Book a free 30-minute video call', action: 'Schedule Now', type: 'link', href: '/schedule-call' },
            { icon: <MessageSquare />, title: 'Quick Chat', desc: 'Ask any question to my Agentic AI Assistant. It\'s trained on my portfolio—ask about projects, skills, or hobbies.', action: 'Start Chat', type: 'link', href: '/agentic-chat' },
          ].map((method) => (
            <TiltCard key={method.title} darkMode={darkMode} glow className={`${darkMode ? 'bg-white/5 border-white/10 text-white hover:shadow-cyan-900/30' : 'bg-white border-gray-200 text-gray-900 hover:shadow-indigo-200'} rounded-2xl p-6 border focus-within:ring-2 ${darkMode ? 'focus-within:ring-cyan-300/50' : 'focus-within:ring-indigo-500'}`}>
              <div className={`${darkMode ? 'bg-white/10 text-white' : 'bg-indigo-50 text-indigo-700'} p-2 rounded-lg w-fit`}>{method.icon}</div>
              <div className="mt-3 font-semibold">{method.title}</div>
              <div className={`${darkMode ? 'text-white/70' : 'text-gray-600'} text-sm mt-1`}>{method.desc}</div>
              <div className="mt-4">
                <Magnetic>
                  {method.type === 'link' ? (
                    <a href={method.href} className={`text-sm px-4 py-2 rounded-lg bg-blue-600 text-white hover:opacity-95 transition inline-block`}>
                      {method.action}
                    </a>
                  ) : (
                    <button className={`text-sm px-4 py-2 rounded-lg bg-blue-600 text-white hover:opacity-95 transition`}>
                      {method.action}
                    </button>
                  )}
                </Magnetic>
              </div>
            </TiltCard>
          ))}
        </section>

        {/* Contact Form */}
        <section id="contact-form" className="mt-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Form */}
            <TiltCard darkMode={darkMode} glow className={`${darkMode ? 'bg-white/5 border-white/10 text-white hover:shadow-cyan-900/30' : 'bg-white border-gray-200 text-gray-900 hover:shadow-indigo-200'} rounded-2xl p-6 border`}>
              <h3 className="text-xl font-bold mb-6">Send Me a Message</h3>
              
              {isSubmitted ? (
                <div className="text-center py-8">
                  <CheckCircle className="mx-auto mb-4 text-green-500" size={48} />
                  <h4 className="text-lg font-semibold mb-2">Message Sent!</h4>
                  <p className={`${darkMode ? 'text-white/70' : 'text-gray-600'}`}>Thank you for reaching out. I'll get back to you within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-white/5 border-white/10 text-white placeholder-white/50' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-white/5 border-white/10 text-white placeholder-white/50' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Company</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-white/5 border-white/10 text-white placeholder-white/50' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                        placeholder="Your company"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Project Link</label>
                      <input
                        type="text"
                        name="project"
                        value={formData.project}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-white/5 border-white/10 text-white placeholder-white/50' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                        placeholder="Paste Link"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Project Details *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-white/5 border-white/10 text-white placeholder-white/50' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none`}
                      placeholder="Tell me about your project, goals, and how I can help..."
                    />
                  </div>
                  
                  <Magnetic>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full px-6 py-3 rounded-lg bg-blue-600 text-white font-medium inline-flex items-center justify-center gap-2 hover:opacity-95 transition focus:outline-none focus:ring-2 ${darkMode ? 'focus:ring-cyan-300/60' : 'focus:ring-indigo-500'} disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message <Send size={16} />
                        </>
                      )}
                    </button>
                  </Magnetic>
                </form>
              )}
            </TiltCard>

            {/* Contact Info */}
            <div id="contact-info" className="space-y-6">
              <TiltCard darkMode={darkMode} glow className={`${darkMode ? 'bg-white/5 border-white/10 text-white hover:shadow-cyan-900/30' : 'bg-white border-gray-200 text-gray-900 hover:shadow-indigo-200'} rounded-2xl p-6 border`}>
                <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className={`${darkMode ? 'bg-white/10 text-white' : 'bg-indigo-50 text-indigo-700'} p-2 rounded-lg`}>
                      <Mail size={16} />
                    </div>
                    <div>
                      <div className="font-medium">Email</div>
                      <div className={`text-sm ${darkMode ? 'text-white/70' : 'text-gray-600'}`}>sikandarktareenwork@gmail.com</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className={`${darkMode ? 'bg-white/10 text-white' : 'bg-indigo-50 text-indigo-700'} p-2 rounded-lg`}>
                      <MapPin size={16} />
                    </div>
                    <div>
                      <div className="font-medium">Location</div>
                      <div className={`text-sm ${darkMode ? 'text-white/70' : 'text-gray-600'}`}>Greater Chicago land Area</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className={`${darkMode ? 'bg-white/10 text-white' : 'bg-indigo-50 text-indigo-700'} p-2 rounded-lg`}>
                      <Phone size={16} />
                    </div>
                    <div>
                      <div className="font-medium">Phone</div>
                      <div className={`text-sm ${darkMode ? 'text-white/70' : 'text-gray-600'}`}>+1 (847)-350-1083</div>
                    </div>
                  </div>
                </div>
              </TiltCard>

              <TiltCard darkMode={darkMode} glow className={`${darkMode ? 'bg-white/5 border-white/10 text-white hover:shadow-cyan-900/30' : 'bg-white border-gray-200 text-gray-900 hover:shadow-indigo-200'} rounded-2xl p-6 border`}>
                <h3 className="text-lg font-semibold mb-4">Follow Me</h3>
                <div className="flex gap-4 justify-start">
                   {[
                       { icon: <Github size={28} />, href: 'https://github.com/sikandarktareen', label: 'GitHub' },
                     { icon: <Linkedin size={28} />, href: 'https://www.linkedin.com/in/sikandar-tareen-54b519134/', label: 'LinkedIn' },
                     { icon: <Globe size={28} />, href: '#', label: 'Portfolio' },
                   ].map((social) => (
                    <Magnetic key={social.label}>
                      <a
                        href={social.href}
                        className={`p-5 rounded-xl ${darkMode ? 'text-white hover:bg-white/10 hover:scale-110' : 'text-gray-700 hover:bg-gray-100 hover:scale-110'} transition-all duration-300 hover:shadow-md`}
                      >
                        {social.icon}
                      </a>
                    </Magnetic>
                  ))}
                </div>
              </TiltCard>

              <TiltCard darkMode={darkMode} className={`${darkMode ? 'from-indigo-600/30 to-cyan-500/30 border-white/10 text-white' : 'from-indigo-100 to-cyan-100 border-gray-200 text-gray-900'} rounded-2xl p-6 bg-gradient-to-r border`}>
                <h4 className="font-semibold mb-3">Prefer a Quick Call?</h4>
                <p className={`${darkMode ? 'text-white/80' : 'text-gray-700'} text-sm mb-4`}>Schedule a free 30-minute video call to discuss your AI project needs.</p>
                <Magnetic>
                  <a href="/schedule-call" className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white shadow hover:opacity-95 transition focus:outline-none focus-visible:ring-2 ${darkMode ? 'focus-visible:ring-cyan-300/60' : 'focus-visible:ring-indigo-500'}`}>
                    <Calendar size={14}/> Book a Call
                  </a>
                </Magnetic>
              </TiltCard>
            </div>
          </div>
        </section>


      </main>

      <footer className={`py-10 text-sm ${darkMode ? 'text-white/60' : 'text-gray-600'} border-t ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">© {new Date().getFullYear()} Sikandar Tareen</div>
      </footer>

      {/* Theme toggle */}
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={toggleTheme}
          className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-lg border transition focus:outline-none focus:ring-2 ${darkMode ? 'border-white/20 bg-[#1a1c20] text-white hover:opacity-90 focus:ring-cyan-300/60' : 'border-gray-300 bg-white/95 backdrop-blur text-gray-800 hover:opacity-90 focus:ring-indigo-500'}`}
          aria-pressed={darkMode}
          aria-label="Toggle theme"
        >
          {darkMode ? <Sun size={16} /> : <Moon size={16} />}
          {darkMode ? 'Light Mode' : 'Dark Mode'}
        </button>
      </div>

      {/* Reduce motion support */}
      <style>{`
        @media (prefers-reduced-motion: reduce) {
          * { animation: none !important; transition: none !important; }
        }
      `}</style>
    </div>
  );
}

function Chip({ children, darkMode }) {
  return (
    <div className={`${darkMode ? 'bg-white/10 border-white/15 text-white' : 'bg-white border-gray-200 text-gray-900'} inline-flex items-center gap-2 px-3 py-1.5 rounded-full border`}>{children}</div>
  );
}

/** TiltCard: 3D tilt on hover with theme-aware glow */
function TiltCard({ children, className = '', glow = false, darkMode = false }) {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const tx = useSpring(rx, { stiffness: 150, damping: 15 });
  const ty = useSpring(ry, { stiffness: 150, damping: 15 });

  function onMove(e) {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width; // 0..1
    const py = (e.clientY - rect.top) / rect.height; // 0..1
    const ROT = 8; // degrees
    rx.set((0.5 - py) * ROT);
    ry.set((px - 0.5) * ROT);
    // propagate mouse position to CSS vars for glow
    el.style.setProperty('--mx', `${Math.round(px * 100)}%`);
    el.style.setProperty('--my', `${Math.round(py * 100)}%`);
  }
  function onLeave() { rx.set(0); ry.set(0); }

  const glowColor = darkMode ? 'rgba(34,211,238,0.28)' : 'rgba(99,102,241,0.25)';

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX: tx, rotateY: ty, transformStyle: 'preserve-3d' }}
      className={`group relative will-change-transform transition-shadow hover:-translate-y-0.5 ${className}`}
    >
      {glow && (
        <div className="pointer-events-none absolute inset-0 rounded-[inherit] blur-2xl opacity-0 group-hover:opacity-40 transition-opacity" style={{
          background: `radial-gradient(520px circle at var(--mx,50%) var(--my,50%), ${glowColor}, transparent 45%)`
        }} />
      )}
      {children}
    </motion.div>
  );
}

/** Magnetic: slight translation toward cursor on hover */
function Magnetic({ children }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 120, damping: 10 });
  const sy = useSpring(y, { stiffness: 120, damping: 10 });

  function onMove(e) {
    const el = ref.current; if (!el) return;
    const rect = el.getBoundingClientRect();
    x.set(((e.clientX - rect.left) / rect.width - 0.5) * 10);
    y.set(((e.clientY - rect.top) / rect.height - 0.5) * 10);
  }
  function onLeave() { x.set(0); y.set(0); }

  return (
    <motion.div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} style={{ x: sx, y: sy }} className="inline-block">
      {children}
    </motion.div>
  );
}

/** Parallax field: subtle floating sparkles */
function ParallaxField({ darkMode }) {
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <div className={`absolute left-1/4 top-10 w-40 h-40 rounded-full ${darkMode ? 'bg-white/5' : 'bg-indigo-100'} blur-3xl animate-[float_10s_ease-in-out_infinite]`} />
      <div className={`absolute right-10 top-1/3 w-24 h-24 rounded-full ${darkMode ? 'bg-indigo-600/20' : 'bg-indigo-200/70'} blur-2xl animate-[float_12s_ease-in-out_infinite]`} />
      <div className={`absolute left-10 bottom-10 w-24 h-24 rounded-full ${darkMode ? 'bg-cyan-500/20' : 'bg-cyan-200/70'} blur-2xl animate-[float_14s_ease-in-out_infinite]`} />
      <style>{`@keyframes float { 0%,100%{ transform: translateY(0)} 50%{ transform: translateY(-12px)} }`}</style>
    </div>
  );
}